import * as React from "react"
import {current_user, ui_actions, ui_state} from "../state/index"
import model_actions from "../state/model_actions"
import {BoardType, Note} from "@cling/lib.shared.model"
import {cancel_event} from "@cling/lib.web.utils"
import {running_on_mobile_device} from "@cling/lib.web.utils"
import {is_elm_inside_an_active_rich_text_editor} from "@cling/lib.web.rich_text_editor"
import {BoardContext} from "../board_context"
import {observer} from "mobx-react"
import {above_card_insert_pos, below_card_insert_pos} from "../card/add_card_actions"
import {board_resource} from "@cling/lib.web.resources"

export const KeyboardShortcuts = observer(() => {
    const {
        current_board: {display_version},
    } = React.useContext(BoardContext)
    const key_down = React.useCallback(
        (e: KeyboardEvent) => {
            if (
                e.altKey ||
                e.ctrlKey ||
                e.metaKey ||
                ui_state.lightbox_shown() ||
                ui_state.dialog !== "none" ||
                ui_state.card_editor ||
                is_elm_inside_an_active_rich_text_editor(e.target as HTMLElement) ||
                document.activeElement?.tagName === "INPUT" ||
                document.activeElement?.tagName === "TEXTAREA"
            ) {
                return
            }
            if (e.key === "/") {
                ui_state.search_state.toggle_search_box()
                e.stopPropagation()
                e.preventDefault()
            } else if (e.key === "b") {
                ui_actions.toggle_desktop_board_chooser("boards", {focus_search: true})
                cancel_event(e)
            } else if (e.key === "p") {
                ui_actions.toggle_desktop_board_chooser("people", {focus_search: true})
                cancel_event(e)
            } else if (e.key === "B") {
                if (ui_state.desktop_board_chooser_state === "hidden") {
                    ui_actions.toggle_desktop_board_chooser("boards")
                } else {
                    ui_actions.toggle_desktop_board_chooser("hidden")
                }
                cancel_event(e)
            }
            if (display_version !== "latest") {
                return
            }
            const current_board = ui_state.current_board.board
            if (!current_board) {
                return
            }
            const current_board_root_card_permissions = current_user.card_permissions(
                current_board,
                display_version,
                current_board.root,
            )
            if (
                e.key === "a" &&
                current_board.board_type !== BoardType.trashcan &&
                !ui_state.active_card &&
                current_board_root_card_permissions.can_add_card
            ) {
                ui_actions.start_adding_pasting_new_card("add")
                cancel_event(e)
                return
            } else if (
                e.key === "v" &&
                current_board.board_type !== BoardType.clipboard &&
                !ui_state.active_card &&
                current_board_root_card_permissions.can_add_card
            ) {
                ui_actions.start_adding_pasting_new_card("paste")
                cancel_event(e)
                return
            }
            const card = ui_state.active_card
            if (!card) {
                return
            }
            const board = board_resource.read(card.board_uid)
            if (!board) {
                return
            }
            let cancel = true
            const permissions = current_user.card_permissions(board, display_version, card)
            if (e.key === ">" && permissions.can_move_card) {
                model_actions.increase_indent_if_possible(
                    board,
                    card,
                    ui_state.search_state.visible_children(card.parent),
                )
            } else if (e.key === "<" && permissions.can_move_card) {
                model_actions.decrease_indent_if_possible(board, card)
            } else if (e.key === "e" && permissions.can_edit_card) {
                ui_actions.start_editing_card(card.board_uid, card.uid)
            } else if (e.key === "r" && permissions.can_remove_card) {
                model_actions.remove_card(board, card)
            } else if (e.key === "h" && permissions.can_archive_card) {
                model_actions.toggle_card_archived(board, card)
            } else if (e.key === "t" && permissions.can_edit_task) {
                ui_actions.show_task_edit_dialog({
                    board,
                    card,
                })
            } else if (e.key === "o") {
                ui_actions.open_comments(card)
            } else if (e.key === " ") {
                ui_actions.toggle_fully_collapsed(card)
            } else if (
                (e.key === "a" || e.key === "A") &&
                board.board_type !== BoardType.trashcan &&
                permissions.can_add_card
            ) {
                const card_uid = model_actions.add_card_locally(
                    board,
                    e.shiftKey
                        ? above_card_insert_pos(card)
                        : below_card_insert_pos(card, {
                              has_visible_children:
                                  ui_state.search_state.has_visible_children(card),
                          }),
                    {note: new Note({})},
                )
                if (card_uid !== "quota_exceeded") {
                    ui_actions.start_editing_card(board.uid, card_uid)
                }
            } else if (
                e.key === "x" &&
                board.board_type !== BoardType.clipboard &&
                permissions.can_cut_card
            ) {
                model_actions.cut_card(board, card)
            } else if (e.key === "c" && permissions.can_copy_card) {
                model_actions.copy_card(board, card)
            } else if (
                (e.key === "v" || e.key === "V") &&
                board.board_type !== BoardType.clipboard &&
                permissions.can_add_card
            ) {
                model_actions.paste_card(
                    board,
                    e.shiftKey
                        ? above_card_insert_pos(card)
                        : below_card_insert_pos(card, {
                              has_visible_children:
                                  ui_state.search_state.has_visible_children(card),
                          }),
                )
            } else {
                cancel = false
            }
            if (cancel) {
                cancel_event(e)
            }
        },
        [display_version],
    )
    React.useEffect(() => {
        if (!running_on_mobile_device()) {
            document.addEventListener("keydown", key_down)
            return () => {
                document.removeEventListener("keydown", key_down)
            }
        }
    }, [key_down])
    return null
})
